/**
 * CONNECTED SYSTEMS (ADR-0024 T3) — a client's books in another system, read with a key the firm gives once.
 *
 * The key is pasted once; the server checks it with the system, keeps it sealed and never shows it again — a
 * connection shows its last four characters. What may be read is chosen when connecting and recorded with the
 * connection. Whether this viewer may connect or disconnect is the server's answer (`viewerMayManage`), and every
 * word about a connection's state — its label, its tone, the names of what it reads — is served, never decided
 * here. Where the deployment connects no system, the section is not shown at all.
 *
 * The page's one saturated control stays its own ("Add the engagement"): connecting is an outline button, and its
 * form opens only when asked for.
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { ConnectionResponse, ConnectionsResponse, type ConnectableSystem, type ConnectionSummary } from "@leveri/contracts";
import { Button, Field, Notice, Reading, RuledList, RuledRow, Section, SectionHead, StateText, TextField, markerTone } from "../file/index.js";
import { call, failureMessage } from "../api.js";

/** "Trial balance", "Ledger transactions" → "trial balance and ledger transactions". */
const reads = (labels: readonly string[]) => {
  const words = labels.map((l) => l.charAt(0).toLowerCase() + l.slice(1));
  if (words.length <= 1) return words.join("");
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
};

function ConnectForm({ clientId, clientName, system, onConnected }: { clientId: string; clientName: string; system: ConnectableSystem; onConnected: () => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  async function connect(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (saving) return;
    const values = new FormData(event.currentTarget);
    const evidenceKinds = system.evidence.map((e) => e.kind).filter((kind) => values.get(`kind:${kind}`) === "on");
    if (evidenceKinds.length === 0) { setFailure("Choose at least one thing Leveri may read."); return; }
    setSaving(true);
    setFailure(null);
    try {
      await call("POST", `/api/clients/${clientId}/connections`, ConnectionResponse, { providerKey: system.providerKey, apiKey: String(values.get("apiKey") ?? ""), evidenceKinds });
      setOpen(false);
      await onConnected();
    } catch (error) {
      setFailure(failureMessage(error));
    } finally {
      setSaving(false);
    }
  }
  if (!open) {
    return (
      <div className="mt-3" data-testid={`connect-${system.providerKey}`}>
        <Button variant="secondary" onClick={() => setOpen(true)}>Connect {system.displayName}</Button>
      </div>
    );
  }
  return (
    <form onSubmit={connect} className="mt-3 grid max-w-[560px] gap-3" data-testid={`connect-${system.providerKey}-form`} autoComplete="off">
      <TextField
        name="apiKey" type="password" label={`${system.displayName} API key`} required disabled={saving} spellCheck={false} autoComplete="off"
        hint={`Make a key in ${system.displayName} for ${clientName} that can read the chart of accounts and journals. Leveri checks it with ${system.displayName} once, keeps it sealed, and never shows it again.`}
      />
      <Field label="Leveri may read">
        <div className="grid gap-1.5">
          {system.evidence.map((e) => (
            <label key={e.kind} className="inline-flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" name={`kind:${e.kind}`} defaultChecked disabled={saving} className="h-4 w-4 accent-forest" />
              {e.label}
            </label>
          ))}
        </div>
      </Field>
      <div className="flex items-center gap-2">
        <Button type="submit" variant="secondary" disabled={saving}>Connect</Button>
        <Button variant="ghost" disabled={saving} onClick={() => { setOpen(false); setFailure(null); }}>Cancel</Button>
      </div>
      {saving && <Reading>Checking the key with {system.displayName}…</Reading>}
      {failure && <Notice>{failure}</Notice>}
    </form>
  );
}

function ConnectionRow({ clientId, connection: c, mayManage, onChanged }: { clientId: string; connection: ConnectionSummary; mayManage: boolean; onChanged: () => Promise<void> }) {
  const [asking, setAsking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  async function disconnect(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (saving) return;
    const reason = String(new FormData(event.currentTarget).get("reason") ?? "").trim();
    setSaving(true);
    setFailure(null);
    try {
      await call("POST", `/api/clients/${clientId}/connections/${c.id}/revoke`, ConnectionResponse, { reason });
      setAsking(false);
      await onChanged();
    } catch (error) {
      setFailure(failureMessage(error));
    } finally {
      setSaving(false);
    }
  }
  const live = c.status !== "REVOKED";
  return (
    <RuledRow className="px-3" data-testid="connection-row">
      <div className="grid grid-cols-1 items-baseline gap-x-6 gap-y-1 md:grid-cols-[minmax(0,1fr)_auto]">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-ink">{c.systemName} · {c.accountName}</div>
          <div className="mt-0.5 text-[13px] text-stone">
            {live ? "Reads" : "Read"} {reads(c.evidence.map((e) => e.label))}{c.keyHint ? ` · key ending ${c.keyHint}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <StateText tone={markerTone(c.tone)} className="text-[13px]">{c.statusLabel}</StateText>
          {live && mayManage && !asking && <Button variant="ghost" size="sm" onClick={() => setAsking(true)}>Disconnect</Button>}
        </div>
      </div>
      {asking && (
        <form onSubmit={disconnect} className="mt-3 grid max-w-[560px] gap-3">
          <Field label="Why" hint="Filed with the disconnection. The key is retired with it and never used again.">
            <textarea name="reason" rows={2} required disabled={saving}
              className="w-full rounded-control border border-line-strong bg-canvas px-3 py-2 text-sm text-ink focus:border-brand" />
          </Field>
          <div className="flex items-center gap-2">
            <Button type="submit" variant="secondary" disabled={saving}>Disconnect</Button>
            <Button variant="ghost" disabled={saving} onClick={() => { setAsking(false); setFailure(null); }}>Cancel</Button>
          </div>
          {saving && <Reading>Disconnecting…</Reading>}
          {failure && <Notice>{failure}</Notice>}
        </form>
      )}
    </RuledRow>
  );
}

/** One client's connected systems: what is connected, and — for those who may — connecting and disconnecting. */
export function ConnectionsSection({ clientId, clientName }: { clientId: string; clientName: string }) {
  const [state, setState] = useState<ConnectionsResponse | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setState(await call("GET", `/api/clients/${clientId}/connections`, ConnectionsResponse));
      setFailure(null);
    } catch (error) {
      setFailure(failureMessage(error));
    }
  }, [clientId]);
  useEffect(() => { void load(); }, [load]);

  if (failure) return <Section><SectionHead>Connected systems</SectionHead><Notice>{failure}</Notice></Section>;
  if (!state || (state.connectable.length === 0 && state.connections.length === 0)) return null;
  const live = state.connections.filter((c) => c.status !== "REVOKED");
  const offerable = state.connectable.filter((s) => !live.some((c) => c.providerKey === s.providerKey));
  return (
    <Section>
      <SectionHead aside={live.length > 0 ? String(live.length) : null}>Connected systems</SectionHead>
      {state.connections.length === 0 && (
        <p className="text-sm text-stone" data-testid="connections-none">
          Connect {clientName}&rsquo;s {offerable.map((s) => s.displayName).join(" or ")} and Leveri reads the books there, so nothing has to be exported.
        </p>
      )}
      {state.connections.length > 0 && (
        <RuledList>
          {state.connections.map((c) => <ConnectionRow key={c.id} clientId={clientId} connection={c} mayManage={state.viewerMayManage} onChanged={load} />)}
        </RuledList>
      )}
      {offerable.length > 0 && (state.viewerMayManage
        ? offerable.map((s) => <ConnectForm key={s.providerKey} clientId={clientId} clientName={clientName} system={s} onConnected={load} />)
        : <p className="mt-3 text-sm text-stone">A member who can grant authority in the firm can connect {clientName}&rsquo;s books.</p>)}
    </Section>
  );
}
